import React from "react";
import {
  MODE_LABELS,
  SEARCH_MODES,
  LOCK_REASONS,
  lockCopy,
  formatCount,
} from "./searchModes";

const SUPPORT_URL = "https://help.documentsync.com";

function Chip({ label, value, tone }) {
  return (
    <span className="ds-chip">
      {label}: <strong>{value}</strong>
      {tone && <span className={`ds-chip-tone ds-chip-${tone.kind}`}>{tone.text}</span>}
    </span>
  );
}

/**
 * Shown in place of an AI mode when the tenant has no DocumentSync AI subscription or the user has no seat.
 * Index Search and Text Search stay available.
 */
export default function AiLockedPanel({
  mode,
  reason,
  entitlement,
  hasTextSearch,
  onSwitchMode,
  onRetry,
}) {
  const tenant = (entitlement && entitlement.tenant) || {};
  const user = (entitlement && entitlement.user) || {};
  const copy = lockCopy(reason, { clientName: entitlement && entitlement.clientName });
  const modeLabel = MODE_LABELS[mode] || "This feature";
  const unavailable = reason === LOCK_REASONS.UNAVAILABLE;

  const tenantTone = unavailable
    ? { kind: "muted", text: "Unknown" }
    : tenant.active
      ? {
          kind: "ok",
          text: tenant.seatLimit
            ? `Active · ${formatCount(tenant.seatsAssigned || 0)} of ${formatCount(tenant.seatLimit)} seats assigned`
            : "Active",
        }
      : { kind: "bad", text: tenant.subscribed ? tenant.status || "Inactive" : "Not subscribed" };
  const userTone = unavailable
    ? { kind: "muted", text: "Unknown" }
    : !tenant.active
      ? { kind: "muted", text: "n/a" }
      : user.hasSeat
        ? { kind: "ok", text: "Assigned" }
        : { kind: "bad", text: "Not assigned" };

  return (
    <div className="ds-locked" id="searchModePanel" role="region" aria-labelledby="lockedTitle">
      <div className="ds-locked-chips">
        <Chip
          label="Tenant"
          value={(entitlement && entitlement.clientName) || "Your organization"}
          tone={tenantTone}
        />
        <Chip label="AI seat" value="Your account" tone={userTone} />
      </div>

      <section className="ds-locked-card">
        <div className="ds-locked-icon" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            <path d="M12 15v2" />
          </svg>
        </div>
        <h2 id="lockedTitle">
          {reason === LOCK_REASONS.NO_USER_SEAT ||
          reason === LOCK_REASONS.QUOTA_EXCEEDED ||
          reason === LOCK_REASONS.SERVICE_DISABLED ||
          unavailable
            ? copy.title
            : `${modeLabel} is part of DocumentSync AI`}
        </h2>
        <p className="ds-locked-body">{copy.body}</p>

        <ul className="ds-locked-features">
          <li>
            <span className="ds-locked-feature-title">Semantic search</span>
            <span>Plain-language queries that match meaning, not just exact words.</span>
          </li>
          <li>
            <span className="ds-locked-feature-title">Grounded answers</span>
            <span>Answers with citations that open the source page in the viewer.</span>
          </li>
          <li>
            <span className="ds-locked-feature-title">Summaries and reports</span>
            <span>Summaries of selected files and reports built from a result set.</span>
          </li>
        </ul>

        <div className="ds-locked-actions">
          {copy.action && (
            <a
              className="btn ds-btn-primary"
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {copy.action}
            </a>
          )}
          {unavailable && (
            <button type="button" className="btn ds-btn-secondary" onClick={onRetry}>
              Check again
            </button>
          )}
          <button
            type="button"
            className="btn ds-btn-secondary"
            onClick={() =>
              onSwitchMode(hasTextSearch ? SEARCH_MODES.TEXT : SEARCH_MODES.INDEX)
            }
          >
            Continue with {hasTextSearch ? "Text Search" : "Index Search"}
          </button>
        </div>
      </section>

      <p className="ds-hint ds-locked-foot">
        Index Search and Text Search are always available.
      </p>
    </div>
  );
}
